import { useState } from 'react'
import { ChevronRight } from 'lucide-react'
import { tokens } from '@/theme'
import type { Capability, Domain } from '@/domain/types'
import { useDomain } from '@/api/hooks/useDomain'
import { useCapabilities } from '@/api/hooks/useCapabilities'
import { Breadcrumb } from '@/components/layout/Breadcrumb'
import { SkeletonList, ErrorPanel } from '@/components/feedback'

interface Props {
  domainId: number
  onBack: () => void
  onSelectCapability: (capabilityId: number) => void
}

/**
 * Detail page for a single domain. Renders the domain card on top and
 * the capabilities-in-this-domain list below. Capability click drills
 * cross-tab into the capability detail.
 */
export function DomainDetailView({ domainId, onBack, onSelectCapability }: Props) {
  const domainQuery = useDomain(domainId)
  // Always show all levels for the domain's capabilities. Level filtering
  // belongs on the cross-domain capabilities tab.
  const capsQuery = useCapabilities({ domainId, page: 1, pageSize: 100 })

  return (
    <>
      <Breadcrumb onBack={onBack}>
        <span style={{ color: tokens.ink3 }}>Domains</span>
        <ChevronRight size={10} color={tokens.ink4} />
        <span style={{ color: tokens.ink, fontWeight: tokens.weightDemi }}>
          {domainQuery.status === 'success' ? domainQuery.data.domainName : '…'}
        </span>
      </Breadcrumb>

      <div
        className="orion-pad"
        style={{
          maxWidth: tokens.containerMax,
          margin: '0 auto',
          padding: `40px ${tokens.containerPadX}px`,
        }}
      >
        {/* Domain header card */}
        {domainQuery.status === 'loading' && <SkeletonList rows={1} rowHeight={140} />}
        {domainQuery.status === 'error' && <ErrorPanel error={domainQuery.error} />}
        {domainQuery.status === 'success' && <DomainHeaderCard domain={domainQuery.data} />}

        {/* Capabilities list */}
        <section style={{ marginTop: 40 }}>
          <SectionHeader
            label="Capabilities in this domain"
            count={capsQuery.status === 'success' ? capsQuery.data.total : null}
          />
          {capsQuery.status === 'loading' && (
            <SkeletonList rows={4} rowHeight={56} maxWidth="100%" />
          )}
          {capsQuery.status === 'error' && <ErrorPanel error={capsQuery.error} />}
          {capsQuery.status === 'success' &&
            (capsQuery.data.data.length === 0 ? (
              <EmptyChild label="No capabilities defined yet for this domain." />
            ) : (
              <CapabilityList capabilities={capsQuery.data.data} onSelect={onSelectCapability} />
            ))}
        </section>
      </div>
    </>
  )
}

// ---------------------------------------------------------------------------
// Bits
// ---------------------------------------------------------------------------

function DomainHeaderCard({ domain }: { domain: Domain }) {
  return (
    <div
      style={{
        padding: '28px 32px',
        background: tokens.paper2,
        border: `1px solid ${tokens.line}`,
        borderRadius: tokens.radiusMd,
        fontFamily: tokens.fontDisplay,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          marginBottom: 16,
        }}
      >
        <div style={{ width: 32, height: 2, background: tokens.gold }} />
        <span
          style={{
            fontSize: 11,
            fontWeight: tokens.weightDemi,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: tokens.gold,
          }}
        >
          Domain
        </span>
        {!domain.isActive && (
          <span
            style={{
              fontSize: 10,
              fontWeight: tokens.weightDemi,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: tokens.ink3,
              background: tokens.paper3,
              border: `1px solid ${tokens.line2}`,
              borderRadius: tokens.radiusSm,
              padding: '2px 8px',
            }}
          >
            Inactive
          </span>
        )}
      </div>
      <h1
        style={{
          fontSize: 30,
          fontWeight: tokens.weightDemi,
          color: tokens.ink,
          margin: 0,
          marginBottom: 8,
        }}
      >
        {domain.domainName}
      </h1>
      {domain.domainDescription && (
        <p
          style={{
            fontSize: tokens.sizeBodyL,
            color: tokens.ink2,
            lineHeight: 1.55,
            maxWidth: 720,
            margin: 0,
          }}
        >
          {domain.domainDescription}
        </p>
      )}
      <div
        style={{
          display: 'flex',
          gap: 32,
          marginTop: 24,
          paddingTop: 20,
          borderTop: `1px solid ${tokens.line}`,
        }}
      >
        <Stat label="Capabilities" value={domain.capabilityCount} />
        <Stat label="Initiatives" value={domain.initiativeCount} />
        <Stat label="Sort order" value={domain.sortOrder} />
        <Stat label="Last modified" value={formatDate(domain.modifiedAt)} />
      </div>
    </div>
  )
}

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <div>
      <div
        style={{
          fontSize: 18,
          fontWeight: tokens.weightDemi,
          color: tokens.ink,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontSize: 10,
          fontWeight: tokens.weightMedium,
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          color: tokens.ink3,
          marginTop: 2,
        }}
      >
        {label}
      </div>
    </div>
  )
}

function SectionHeader({ label, count }: { label: string; count: number | null }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'baseline',
        gap: 12,
        marginBottom: 16,
        paddingBottom: 10,
        borderBottom: `1px solid ${tokens.line}`,
      }}
    >
      <h2
        style={{
          fontSize: tokens.sizeH3,
          fontWeight: tokens.weightDemi,
          color: tokens.ink,
          margin: 0,
        }}
      >
        {label}
      </h2>
      {count !== null && (
        <span
          style={{
            fontSize: 12,
            color: tokens.ink3,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {count}
        </span>
      )}
    </div>
  )
}

function CapabilityList({
  capabilities,
  onSelect,
}: {
  capabilities: Capability[]
  onSelect: (id: number) => void
}) {
  return (
    <ul style={{ listStyle: 'none', display: 'grid', gap: 6, padding: 0, margin: 0 }}>
      {capabilities.map(c => (
        <CapabilityRow key={c.capabilityId} cap={c} onClick={() => onSelect(c.capabilityId)} />
      ))}
    </ul>
  )
}

function CapabilityRow({ cap, onClick }: { cap: Capability; onClick: () => void }) {
  const [hover, setHover] = useState(false)
  const indent = cap.capabilityLevel === 2 ? 28 : 0

  return (
    <li>
      <button
        onClick={onClick}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr auto auto',
          alignItems: 'center',
          gap: 16,
          width: '100%',
          textAlign: 'left',
          padding: `12px 16px 12px ${16 + indent}px`,
          background: hover ? tokens.paper2 : tokens.paper,
          border: `1px solid ${hover ? tokens.line2 : tokens.line}`,
          borderRadius: tokens.radiusMd,
          cursor: 'pointer',
          fontFamily: tokens.fontDisplay,
          transition: `all ${tokens.durFast} ${tokens.easeStandard}`,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 14,
              fontWeight: cap.capabilityLevel === 1 ? tokens.weightDemi : tokens.weightMedium,
              color: tokens.ink,
            }}
          >
            <LevelChip level={cap.capabilityLevel} />
            {cap.capabilityName}
          </div>
          {cap.capabilityDescription && (
            <div
              style={{
                fontSize: 12,
                color: tokens.ink3,
                marginTop: 2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {cap.capabilityDescription}
            </div>
          )}
        </div>
        <span
          style={{
            fontSize: 12,
            color: tokens.ink3,
            fontVariantNumeric: 'tabular-nums',
            minWidth: 70,
            textAlign: 'right',
          }}
        >
          {cap.initiativeCount} init.
        </span>
        <ChevronRight size={14} strokeWidth={1.6} color={hover ? tokens.gold : tokens.ink4} />
      </button>
    </li>
  )
}

function LevelChip({ level }: { level: number }) {
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: tokens.weightDemi,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        color: tokens.ink3,
        background: tokens.paper3,
        border: `1px solid ${tokens.line2}`,
        borderRadius: tokens.radiusSm,
        padding: '2px 6px',
      }}
    >
      L{level}
    </span>
  )
}

function EmptyChild({ label }: { label: string }) {
  return (
    <div
      style={{
        padding: '32px 20px',
        textAlign: 'center',
        color: tokens.ink3,
        fontFamily: tokens.fontDisplay,
        fontSize: 13,
        background: tokens.paper2,
        border: `1px dashed ${tokens.line2}`,
        borderRadius: tokens.radiusMd,
      }}
    >
      {label}
    </div>
  )
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, { dateStyle: 'medium' })
  } catch {
    return iso
  }
}
